import React from "react";
import { ClipboardListIcon } from "@heroicons/react/solid";

const Resume = () => {
  const coreSkills = [
    "Backend Engineering: PHP, Laravel, MySQL, GraphQL",
    "Frontend Engineering: JavaScript, TypeScript, React, jQuery",
    "Programming: PHP, Python, Ruby",
    "Testing and Quality: PHPUnit, Jest, test-driven development, low-defect delivery",
    "DevOps and Cloud: AWS, Lambda, Docker, GitHub Actions, Terraform (foundational), CI/CD pipelines",
    "Delivery: Feature ownership, estimation, collaboration with QA and product, release support",
    "Technical Practices: Code review, mentoring, refactoring, pragmatic AI-assisted development",
    "Version Control: Git",
  ];

  const currentPosition = [
    "Own end-to-end delivery of backend-led features, from design through to production.",
    "Build and maintain Laravel services with strong automated test coverage and low defect rates.",
    "Design and support GraphQL APIs used in production, including high-traffic queries and mutations with authorisation, error handling, and performance-aware pagination.",
    "Implement background processing using Laravel Jobs for long-running workflows.",
    "Deliver safe production data migrations using batched, test-backed commands.",
    "Build and operate AWS Lambda integrations and support deployments via CI/CD pipelines.",
    "Work closely with QA and product to refine scope, estimate work, and support regular releases.",
    "Apply disciplined refactoring and TDD to improve long-term maintainability and system stability.",
    "Contribute to architectural discussions and early technical planning.",
  ];

  const professionalExperience = [
    {
      title: "Backend Developer, ECO Surv",
      period: "2022 - 2023",
      points: [
        "Transitioned into a hands-on backend role following the successful migration from a legacy platform to a modern Laravel-based system.",
        "Built and maintained core backend services using Laravel.",
        "Helped establish practical development and testing standards across the team.",
        "Improved code quality through structured code reviews and increased automated test coverage.",
        "Contributed to backend migrations and ongoing system modernisation work.",
      ],
    },
    {
      title: "Lead Developer, ECO Surv",
      period: "2020 - 2022",
      points: [
        "Led the maintenance and controlled wind-down of a large legacy PHP and Laravel platform.",
        "Line-managed engineers, providing one-to-ones, appraisals, and technical guidance.",
        "Designed and delivered new features while responding to production incidents.",
        "Owned core data management, database administration, and AWS support.",
        "Improved reliability through consistent testing and code review practices.",
      ],
    },
    {
      title: "Developer, ECO Surv",
      period: "2018 - 2020",
      points: [
        "Worked on the ongoing development and improvement of a relatively simple but evolving production platform.",
        "Developed and maintained application features using PHP, JavaScript, and jQuery.",
        "Took on broad responsibility across the codebase, contributing to new functionality, fixes, and incremental improvements.",
        "Supported live systems, including issue investigation, bug fixes, and routine platform support.",
      ],
    },
    {
      title: "Apprentice Software Developer, ECO Surv",
      period: "2015 - 2018",
      points: [
        "Built and enhanced PDF generation functionality using PHP.",
        "Developed features using PHP, JavaScript, and jQuery under senior developer guidance.",
        "Assisted with bug fixing, platform support, and incremental feature improvements.",
        "Gained hands-on experience across the full development lifecycle in a commercial environment.",
      ],
    },
  ];

  const keyAchievements = [
    "Led multiple production data migrations, safely transforming thousands of records with no data loss.",
    "Built a Python automation to process and convert hundreds of thousands of images in AWS S3.",
    "Designed and optimised high-traffic GraphQL resolvers with predictable pagination.",
    "Built CI/CD pipelines using GitHub Actions, Docker, Terraform, and AWS OIDC.",
    "Improved test reliability by removing flaky patterns and aligning tests with production behaviour.",
    "Raised engineering standards through mentoring and constructive code reviews.",
  ];

  return (
    <div
      id="Resume"
      className="lg:w-3/4 mt-64 mb-4 text-justify mx-12 lg:mr-60 lg:ml-auto"
    >
      <div>
        <div className="table">
          <ClipboardListIcon className="h-5 w-5 mr-4 text-yellow_vs" />{" "}
          <code className="table-cell text-[#e6f1ff] text-3xl mt-5 whitespace-nowrap">
            Resume
          </code>
          <div className="table-cell border-b border-b-[#e6f1ff] border-opacity-25 w-full"></div>
        </div>
      </div>
      <div className="text-[#a2aabc] text-lg mt-5">
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Profile</code>
          </div>
          <div className="w-3/4">
            <code className="italic text-[#80CBC4]">
              Eathan Atkinson
              <br />
              Full Stack Developer (Backend and DevOps Focus)
            </code>
            <br />
            <code className="text-sm text-lightblue_vs">
              Lancashire | eathan.atkinson@gmail.com
            </code>
          </div>
        </div>
        <br />
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Professional Summary</code>
          </div>
          <div className="w-3/4">
            <code className="text-sm">
              I am a full stack developer with commercial experience since 2015,
              primarily focused on backend engineering and AWS-based delivery. My
              core experience is in PHP and Laravel, alongside JavaScript,
              TypeScript, and React. I regularly take features from early design
              through to production release, owning them end to end. I care
              about building systems that are reliable, well tested, and
              straightforward to maintain. Colleagues trust me for clear
              technical judgement, thoughtful problem solving, and improving how
              teams deliver through better testing, automation, and
              infrastructure practices. I am comfortable working across the
              stack, estimating work realistically, and using modern tooling,
              including AI, to speed up development while keeping standards
              high.
            </code>
          </div>
        </div>
        <br />
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Core Skills</code>
          </div>
          <div className="w-3/4">
            {coreSkills.map((skill) => (
              <code key={skill} className="text-sm block mb-3">
                - {skill}
              </code>
            ))}
          </div>
        </div>
        <br />
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Current Position</code>
          </div>
          <div className="w-3/4">
            <code className="italic text-[#80CBC4]">
              Full Stack Developer, ECO Surv
            </code>
            <br />
            <code className="text-sm text-lightblue_vs">2023 - Present</code>
            <br />
            <br />
            {currentPosition.map((item) => (
              <code key={item} className="text-sm block mb-3">
                - {item}
              </code>
            ))}
          </div>
        </div>
        <br />
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Professional Experience</code>
          </div>
          <div className="w-3/4">
            {professionalExperience.map((role) => (
              <div key={role.title} className="mb-6">
                <code className="italic text-[#80CBC4]">{role.title}</code>
                <br />
                <code className="text-sm text-lightblue_vs">{role.period}</code>
                <br />
                <br />
                {role.points.map((point) => (
                  <code key={point} className="text-sm block mb-3">
                    - {point}
                  </code>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Education</code>
          </div>
          <div className="w-3/4">
            <code className="italic text-[#80CBC4]">
              Level 3 Software Development Technician Apprenticeship
            </code>
            <br />
            <code className="text-sm">
              Equivalent to a Level 3 Diploma in Software Development
            </code>
            <br />
            <br />
            <code className="text-sm">
              8 GCSEs at grades A-C (or equivalent), including Math and English
            </code>
          </div>
        </div>
        <br />
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Key Achievements</code>
          </div>
          <div className="w-3/4">
            {keyAchievements.map((achievement) => (
              <code key={achievement} className="text-sm block mb-3">
                - {achievement}
              </code>
            ))}
          </div>
        </div>
        <br />
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Professional Development</code>
          </div>
          <div className="w-3/4">
            <code className="text-sm">
              Working towards AWS Certified Solutions Architect - Associate
            </code>
            <br />
            <br />
            <code className="text-sm">
              Ongoing focus on backend architecture, DevOps workflows, and
              scalable system design
            </code>
          </div>
        </div>
        <br />
        <div className="flex flex-row">
          <div className="w-1/4">
            <code className="text-yellow_vs">Career Focus</code>
          </div>
          <div className="w-3/4">
            <code className="text-sm">
              Target roles: Senior Backend Engineer, Backend Engineer with
              DevOps focus.
            </code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Resume;
